import { JobV2 } from "@/app/@types/job";
import { db } from "@/app/_firebase/firebase";
import { getJobByJobUId } from "@/app/_firebase/jobs";
import { getChangedFields } from "@/app/_helpers/deepCompare";
import { formatCamelCaseString } from "@/app/_helpers/string";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import { doc, onSnapshot } from "firebase/firestore";
import { useEffect } from "react";
import { useSetAlert } from "../useAlertContext";
import { useUserContext } from "../useUserContext";
import { getTabId } from "@/app/_helpers/tabId";

export const useGetJobByJobUid = (userId: string, jobUid: string) => {
  const user = useUserContext();
  const setAlert = useSetAlert();
  const queryClient = useQueryClient();
  const {
    data,
    error,
    isPending: isLoading,
    isError,
    refetch,
  } = useQuery<JobV2 | null, Error>({
    queryKey: ["jobs", jobUid],
    queryFn: () => getJobByJobUId(userId, jobUid),
    enabled: !!userId && !!jobUid, // Ensure that we have a valid userId and jobUid
    staleTime: 3600000, // 1 hour in milliseconds
    gcTime: 5 * 60 * 1000, // Garbage collection time, replaced cacheTime with gcTime
  });

  useEffect(() => {
    if (!data?.uid) return; // Ensure data.uid is defined before proceeding
    const jobsDocRef = doc(db, "users", user.uid, "jobs", data.uid);

    let previousData: JobV2 | null = null;

    const unsubscribe = onSnapshot(
      jobsDocRef,
      (snapshot) => {
        if (snapshot.exists()) {
          const newData = snapshot.data() as JobV2;
          console.log({ newData });
          queryClient.setQueryData(["jobs", jobUid], newData);
          if (previousData) {
            const lastHistoryEvent =
              newData.history[newData.history.length - 1];

            //  this means we changed a job straight from firebase GUI
            if (!lastHistoryEvent) return;

            const teamMember = lastHistoryEvent.teamMember.uid;

            // Compare tabs, not users. Teams that share one login would
            // otherwise never be warned, which is exactly where concurrent
            // edits silently overwrite each other.
            if (newData.lastWriteTabId !== getTabId()) {
              const changedFields = getChangedFields(newData, previousData);
              const changedKeys = Object.keys(changedFields)
                .filter(
                  (key) =>
                    key !== "history" &&
                    key !== "updatedAt" &&
                    key !== "lastWriteTabId" &&
                    // Appended by the send routes and delivery/open webhooks
                    // through the Admin SDK, which cannot stamp this tab's id.
                    // Not form data, and updateJobV2 carries the latest copy
                    // forward, so sending an invoice from this tab must not
                    // warn about itself.
                    key !== "communications"
                )
                .map((key) =>
                  key === "lineItems"
                    ? "Mounts/Services"
                    : key === "payments"
                    ? "Payments"
                    : formatCamelCaseString(key)
                );

              // Nothing but communications moved: refresh silently below.
              if (changedKeys.length > 0) {
                const alertContent =
                  changedKeys.join(", ") +
                  (changedKeys.length > 1 ? " were updated" : " was updated") +
                  ". Anything you save from this tab is based on the older " +
                  "version and will overwrite it.";

                // A write from the same account but a different tab is either
                // this person in another window or a colleague on a shared
                // login. We cannot tell those apart, so say neither.
                const writerName = lastHistoryEvent.teamMember.fullName;
                const title =
                  teamMember === "QuickBooks"
                    ? "Job was updated by QuickBooks"
                    : teamMember === user.details.uid
                    ? "Job was updated in another window or tab"
                    : `Job was updated by ${writerName || "a team member"}`;

                setAlert({
                  type: "warning",
                  title,
                  content: alertContent,
                  // Stays until dismissed. The job open in this tab is now out
                  // of date, and saving it would overwrite the change just
                  // made. A toast that fades after 7.5s is missed exactly when
                  // it matters.
                  persistent: true,
                });
              }
            }
          }

          // Update previousData to the current snapshot's data
          previousData = newData;
        } else {
          // console.log("Document does not exist!");
        }
      },
      (error) => {
        //  TODO: idk what we'd even do here
      }
    );

    return () => {
      unsubscribe();
    };
  }, [user.uid, data?.uid, queryClient, jobUid]);

  return { data, error, isLoading, isError, refetch };
};
